import { clsx } from 'clsx';
import Stack from '../../_components/Stack';
import { Button } from '../Button';
import { FeaturedIcon } from '../FeaturedIcon';
import { Typography } from '../Typography';
import { containerVariants, descriptionVariants, titleVariants } from './styles';
import type { EmptyStateDeprecatedProps, EmptyStateSizes } from './types';

const getTitleVariant = (size: EmptyStateSizes) => {
  switch (size) {
    case 'xs':
      return 'text-sm';
    case 'sm':
      return 'text-md';
    case 'lg':
      return 'text-xl';
  }
};

const getDescriptionVariant = (size: EmptyStateSizes) => {
  switch (size) {
    case 'xs':
      return 'text-xs';
    case 'sm':
      return 'text-sm';
    case 'lg':
      return 'text-md';
  }
};

const getButtonSize = (size: EmptyStateSizes) => {
  switch (size) {
    case 'xs':
      return 'sm';
    case 'sm':
      return 'md';
    case 'lg':
      return 'lg';
  }
};

const getFeaturedIconSize = (size: EmptyStateSizes) => {
  switch (size) {
    case 'xs':
      return 'md';
    case 'sm':
      return 'lg';
    case 'lg':
      return 'xl';
  }
};

function EmptyState(props: EmptyStateDeprecatedProps) {
  const {
    title,
    description,
    Icon,
    icon,
    size = 'sm',
    variant = 'hug',
    className,
    primaryButton,
    secondaryButton,
    slotProps,
  } = props;

  const containerClassName = containerVariants({ className, size, variant });
  const titleClassName = titleVariants({ size });
  const descriptionClassName = descriptionVariants({ size });

  // combine the check?
  const featuredIconSize = getFeaturedIconSize(size);
  const titleVariant = getTitleVariant(size);
  const descriptionVariant = getDescriptionVariant(size);
  const buttonSize = getButtonSize(size);

  const { label: primaryLabel, ...primaryRest } = primaryButton ?? {};
  const { label: secondaryLabel, ...secondaryRest } = secondaryButton ?? {};

  return (
    <Stack
      alignItems="center"
      className={containerClassName}
      direction="column"
      justifyContent="center"
    >
      {icon ??
        (Icon ? (
          <FeaturedIcon Icon={Icon} size={featuredIconSize} {...slotProps?.featuredIcon} />
        ) : null)}
      <Typography
        className={clsx(titleClassName, slotProps?.title?.className)}
        color="text-primary"
        variant={titleVariant}
        weight="semi-bold"
      >
        {title}
      </Typography>
      {description ? (
        <Typography
          className={clsx(descriptionClassName, slotProps?.description?.className)}
          color="text-tertiary"
          variant={descriptionVariant}
          weight="regular"
        >
          {description}
        </Typography>
      ) : null}
      {primaryButton || secondaryButton ? (
        <Stack
          className={clsx('gap-x-lg', slotProps?.actionsContainer?.className)}
          direction="row"
          justifyContent="center"
        >
          {secondaryButton ? (
            <Button color="neutral" size={buttonSize} variant="outline" {...secondaryRest}>
              {secondaryLabel}
            </Button>
          ) : null}
          {primaryButton ? (
            <Button color="brand" size={buttonSize} variant="solid" {...primaryRest}>
              {primaryLabel}
            </Button>
          ) : null}
        </Stack>
      ) : null}
    </Stack>
  );
}

export default EmptyState;
